import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, FileUp, RefreshCcw, UploadCloud, X } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { BankAccountPicker } from '@/components/BankAccountPicker'
import { Modal } from '@/components/Modal'
import { Table } from '@/components/ui/table'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { EmptyState } from '@/components/EmptyState'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/cn'
import { ApiError } from '@/lib/api'
import { formatCurrencyPrecise, formatDate } from '@/lib/locale'
import { useCoa, useCreateSubcategory } from '@/features/coa/queries'
import type { TaxonomyItem } from '@/features/coa/types'
import { uploadApi, uploadStatementFile } from '@/features/upload/api'
import type {
  ClassifiedRow,
  ClassifiedTable,
  CoaSuggestion,
  DuplicateRow,
  ExtractedTable,
  StatementByFileRow,
  StatementTypeCode,
  StatusResponse,
} from '@/features/upload/types'
import type { ClassificationGroup, CoaSelection } from './ClassificationGroupCard'
import { ClassifyStepper } from './ClassifyStepper'

type Step = 'form' | 'extracting' | 'review' | 'duplicates' | 'classify' | 'confirming' | 'results'

const TERMS_KEY = 'upload_terms_accepted'

function prettyErr(err: unknown, fallback = 'Something went wrong.'): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error) return err.message
  return fallback
}

function rowDescription(row: { description?: string }): string {
  return (row.description ?? '').trim()
}

function rowKey(tIdx: number, rIdx: number): string {
  return `${tIdx}-${rIdx}`
}

function regexNormalizeDescription(desc: string): string {
  return desc
    .replace(/(Ref|ID|#)\s*#?\s*[A-Z0-9-]{3,}/gi, '')
    .replace(/\d{4,}/g, '')
    .replace(/\d{1,2}\/\d{1,2}(\/\d{2,4})?/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function isValidPrediction(suggs: CoaSuggestion[] | undefined): boolean {
  if (!suggs || suggs.length === 0) return false
  return suggs.some(
    (s) =>
      s.main_category &&
      s.main_category !== 'Uncategorized' &&
      s.subcategory &&
      s.subcategory !== 'General',
  )
}

function filterValidPredictions(suggs: CoaSuggestion[] | undefined): CoaSuggestion[] {
  if (!suggs) return []
  return suggs.filter(
    (s) =>
      s.main_category &&
      s.main_category !== 'Uncategorized' &&
      s.subcategory &&
      s.subcategory !== 'General',
  )
}

function resolveCodeFromTaxonomy(s: CoaSuggestion, taxonomy: TaxonomyItem[]): CoaSuggestion {
  if (s.account_code && s.account_code !== 'N/A') return s
  const targetSub = (s.subcategory ?? '').trim().toLowerCase()
  if (!targetSub || targetSub === 'general') return s
  const exact = taxonomy.find(
    (t) =>
      t.subcategory_name.toLowerCase() === targetSub &&
      t.main_category === s.main_category &&
      t.statement_type === s.statement_type &&
      t.account_code,
  )
  if (exact) return { ...s, account_code: exact.account_code }
  const sameStmt = taxonomy.find(
    (t) =>
      t.subcategory_name.toLowerCase() === targetSub &&
      t.statement_type === s.statement_type &&
      t.account_code,
  )
  if (sameStmt) {
    return {
      ...s,
      main_category: sameStmt.main_category,
      account_code: sameStmt.account_code,
    }
  }
  const anywhere = taxonomy.find(
    (t) => t.subcategory_name.toLowerCase() === targetSub && t.account_code,
  )
  if (anywhere) {
    return {
      statement_type: anywhere.statement_type,
      main_category: anywhere.main_category,
      subcategory: anywhere.subcategory_name,
      account_code: anywhere.account_code,
    }
  }
  return s
}

function resultsStats(rows: StatementByFileRow[]): { income: number; expense: number } {
  let income = 0
  let expense = 0
  for (const r of rows) {
    const n = parseFloat(String(r.amount ?? 0)) || 0
    if (n >= 0) income += n
    else expense += Math.abs(n)
  }
  return { income, expense }
}

export function UploadPage() {
  const { toast } = useToast()
  const { data: taxonomyRows } = useCoa()
  const createSubcategory = useCreateSubcategory()
  const taxonomy = useMemo(() => taxonomyRows ?? [], [taxonomyRows])

  // Form state
  const [statementType, setStatementType] = useState<StatementTypeCode | ''>('')
  const [period, setPeriod] = useState('')
  const [accountNo] = useState('')
  const [bankAccountId, setBankAccountId] = useState<number | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  // Wizard state
  const [step, setStep] = useState<Step>('form')
  const [s3FileId, setS3FileId] = useState<number | null>(null)
  const [tables, setTables] = useState<ExtractedTable[]>([])
  const [excludedTables, setExcludedTables] = useState<Set<number>>(new Set())
  const [classifiedTables, setClassifiedTables] = useState<ClassifiedTable[]>([])
  const [duplicates, setDuplicates] = useState<DuplicateRow[]>([])
  const [newTxns, setNewTxns] = useState<DuplicateRow[]>([])
  const [results, setResults] = useState<StatementByFileRow[]>([])
  const [limitReached, setLimitReached] = useState(false)

  // Grouped classification state
  const [classGroups, setClassGroups] = useState<ClassificationGroup[]>([])
  const [classSelections, setClassSelections] = useState<Record<string, CoaSelection | null>>({})

  // Consent modal
  const [consentOpen, setConsentOpen] = useState(false)
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false)

  // Polling
  const [statusText, setStatusText] = useState<string>('Initializing AI engine…')
  const pollAbortRef = useRef<{ stop: boolean }>({ stop: false })

  const resetWizard = () => {
    setFile(null)
    setStep('form')
    setS3FileId(null)
    setTables([])
    setExcludedTables(new Set())
    setClassifiedTables([])
    setDuplicates([])
    setNewTxns([])
    setResults([])
    setLimitReached(false)
    setClassGroups([])
    setClassSelections({})
    pollAbortRef.current.stop = true
    pollAbortRef.current = { stop: false }
  }

  function pickFile(f: File | null) {
    if (!f) return setFile(null)
    const allowed = ['.pdf', '.csv', '.xlsx', '.xls', '.xlsb', '.xlsm']
    const ext = '.' + f.name.split('.').pop()?.toLowerCase()
    if (!allowed.includes(ext)) {
      toast({
        variant: 'destructive',
        title: 'Invalid file type',
        description: 'Please upload a PDF, CSV, or Excel file.',
      })
      return
    }
    if (f.size > 10 * 1024 * 1024) {
      toast({ variant: 'destructive', title: 'File too large', description: 'Max 10 MB.' })
      return
    }
    setFile(f)
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files[0]) pickFile(e.dataTransfer.files[0])
  }

  function submitForm(e: React.FormEvent) {
    e.preventDefault()
    if (!statementType) return toast({ variant: 'destructive', title: 'Select a statement type.' })
    if (!period) return toast({ variant: 'destructive', title: 'Enter a statement date.' })
    if (!bankAccountId && !/^\d{4}$/.test(accountNo))
      return toast({ variant: 'destructive', title: 'Pick a bank account or add a new one.' })
    if (!file) return toast({ variant: 'destructive', title: 'Choose a file to upload.' })
    if (!localStorage.getItem(TERMS_KEY)) {
      setConsentOpen(true)
      return
    }
    void executeUpload()
  }

  async function pollStatusUntilExtracted(fileId: number): Promise<ExtractedTable[]> {
    setStatusText('Uploading file to secure vault…')
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (pollAbortRef.current.stop) throw new Error('Cancelled')
      await new Promise((r) => setTimeout(r, 2000))
      const data: StatusResponse = await uploadApi.status(fileId)
      if (data.status === 'pending' || data.status === 'queued') setStatusText('In queue - waiting for AI worker…')
      else if (data.status === 'processing') setStatusText('Worker assigned - downloading document…')
      else if (data.status === 'extracting') setStatusText('AI reading pages and detecting tables…')
      else if (data.status === 'extracted') return data.tables ?? []
      else if (data.status === 'failed') throw new Error(data.error || 'Extraction failed')
    }
  }

  async function executeUpload() {
    try {
      setStep('extracting')
      setStatusText('Uploading and analyzing file…')
      setLimitReached(false)
      const { response, status } = await uploadStatementFile({
        file: file as File,
        statement_type: statementType,
        period,
        account_no: accountNo || undefined,
        bank_account_id: bankAccountId ?? undefined,
      })
      setS3FileId(response.s3_file_id)
      if (status === 200 && 'tables' in response && response.tables) {
        if (response.tables.length === 0) throw new Error('No transaction data detected.')
        setTables(response.tables)
        setStep('review')
      } else {
        // 202 - queued; poll until extracted
        const t = await pollStatusUntilExtracted(response.s3_file_id)
        if (t.length === 0) throw new Error('No transaction tables detected in the document.')
        setTables(t)
        setStep('review')
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setLimitReached(true)
        setStep('form')
      } else {
        toast({
          variant: 'destructive',
          title: 'Upload failed',
          description: prettyErr(err),
        })
        setStep('form')
      }
    }
  }

  function toggleExcludeTable(idx: number, excluded: boolean) {
    setExcludedTables((prev) => {
      const next = new Set(prev)
      if (excluded) next.add(idx)
      else next.delete(idx)
      return next
    })
  }

  async function confirmTables() {
    const selected: ExtractedTable[] = tables
      .map((t, i) => ({ t, i }))
      .filter(({ i }) => !excludedTables.has(i))
      .map(({ t }) => ({ ...t, rows: [...t.rows] }))
    if (selected.length === 0) {
      return toast({
        variant: 'destructive',
        title: 'Selection required',
        description: 'Select at least one table to process.',
      })
    }
    try {
      const dup = await uploadApi.checkDuplicates(selected)
      if (dup.duplicates.length > 0 && dup.newTransactions.length > 0) {
        setDuplicates(dup.duplicates)
        setNewTxns(dup.newTransactions)
        setDuplicateModalOpen(true)
        return
      }
      if (dup.newTransactions.length === 0 && dup.duplicates.length > 0) {
        toast({
          title: 'All duplicates',
          description: 'This data is already processed and saved.',
        })
        resetWizard()
        return
      }
      await analyzeAndClassify(selected)
    } catch (err) {
      toast({ variant: 'destructive', title: 'Processing failed', description: prettyErr(err) })
    }
  }

  async function proceedWithFilter(filterFn: (tIdx: number, rIdx: number) => boolean) {
    const selected: ClassifiedTable[] = tables
      .map((t, i) => ({
        name: t.name,
        page: t.page,
        rows: t.rows.filter((_, rIdx) => filterFn(i, rIdx)) as ClassifiedRow[],
      }))
      .filter((t) => t.rows.length > 0)
    setDuplicateModalOpen(false)
    if (selected.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No transactions',
        description: 'Please select at least one transaction to proceed.',
      })
      return
    }
    await analyzeAndClassify(selected)
  }

  async function analyzeAndClassify(selected: ClassifiedTable[]) {
    setStatusText('Analyzing descriptions…')
    setStep('extracting')

    // Collect descriptions
    const allDescriptions: string[] = []
    for (const t of selected) for (const r of t.rows) if (rowDescription(r)) allDescriptions.push(rowDescription(r))

    // Step A: match learned rules first - these auto-classify with no user review
    let matches: Record<string, { general_category: string; coa: { statement_type: string; main_category: string; subcategory: string; account_code: string } | null }> = {}
    if (allDescriptions.length > 0) {
      try {
        matches = await uploadApi.matchBatch(allDescriptions)
      } catch {
        /* silent - legacy also swallows this */
      }
    }

    // Apply learned-rule matches; collect rows still needing classification
    const unmatchedRows: Array<{ tIdx: number; rIdx: number; row: ClassifiedRow; description: string }> = []
    selected.forEach((t, tIdx) => {
      t.rows.forEach((r, rIdx) => {
        const desc = rowDescription(r)
        const rule = matches[desc]
        if (rule && rule.coa) {
          r.general_category = rule.general_category
          r.category = rule.general_category
          r.coa = rule.coa
        } else if (desc) {
          r.coa = null
          r.category = r.category ?? rule?.general_category ?? ''
          unmatchedRows.push({ tIdx, rIdx, row: r, description: desc })
        }
      })
    })

    setClassifiedTables(selected)

    if (unmatchedRows.length === 0) {
      void submitClassifiedTables(selected)
      return
    }

    setStatusText('Grouping similar transactions…')

    // Step B: normalize-batch to group similar transactions
    const uniqueDescriptions = Array.from(new Set(unmatchedRows.map((u) => u.description)))
    let normalizedMap: Record<string, string> = {}
    try {
      const res = await uploadApi.normalizeDescriptionBatch(uniqueDescriptions)
      normalizedMap = res.normalized ?? {}
    } catch {
      /* fall back to regex normalization */
    }

    // Build groups by normalized description (1st row in a group = "main")
    const groupMap = new Map<string, ClassificationGroup>()
    for (const u of unmatchedRows) {
      const normalized = normalizedMap[u.description] ?? regexNormalizeDescription(u.description)
      const existing = groupMap.get(normalized)
      const itemWithKey = { ...u.row, tIdx: u.tIdx, rIdx: u.rIdx }
      if (existing) {
        existing.children.push(itemWithKey)
      } else {
        groupMap.set(normalized, {
          key: normalized || `row-${u.tIdx}-${u.rIdx}`,
          main: itemWithKey,
          children: [],
          suggestions: [],
        })
      }
    }

    setStatusText('AI is mapping Chart of Accounts…')

    // Step C: predict COA suggestions for each group's main description
    const payload = Array.from(groupMap.values()).map((g) => ({
      description: g.main.description ?? '',
      category: g.main.category ?? undefined,
    }))
    let coaPredictions: Record<string, CoaSuggestion[]> = {}
    try {
      const res = await uploadApi.predictCoaBatch(payload)
      coaPredictions = res.predictions ?? {}
    } catch {
      /* silent - leave suggestions empty so manual picker is shown */
    }

    const findPrediction = (desc: string): CoaSuggestion[] => {
      const trimmed = desc.trim()
      if (coaPredictions[desc]) return coaPredictions[desc]
      if (coaPredictions[trimmed]) return coaPredictions[trimmed]
      const entry = Object.entries(coaPredictions).find(([k]) => k.trim() === trimmed)
      return entry ? entry[1] : []
    }

    const groups = Array.from(groupMap.values()).map((g) => {
      const raw = findPrediction(g.main.description ?? '')
      let suggestions: CoaSuggestion[] = []
      if (isValidPrediction(raw)) {
        suggestions = filterValidPredictions(raw).map((s) => resolveCodeFromTaxonomy(s, taxonomy))
      }
      return { ...g, suggestions }
    })

    // Pre-select first valid suggestion for each group
    const initialSelections: Record<string, CoaSelection | null> = {}
    for (const g of groups) {
      if (g.suggestions.length > 0) {
        const top = g.suggestions[0]
        initialSelections[g.key] = {
          statement_type: top.statement_type,
          main_category: top.main_category,
          subcategory: top.subcategory,
          account_code: top.account_code ?? '',
        }
      } else {
        initialSelections[g.key] = null
      }
    }

    setClassGroups(groups)
    setClassSelections(initialSelections)
    setStep('classify')
  }

  async function saveClassifications() {
    // Validate every group has a selection
    for (const g of classGroups) {
      if (!classSelections[g.key]) {
        return toast({
          variant: 'destructive',
          title: 'COA required',
          description: `Please pick a Chart of Account for "${g.main.description ?? '(no description)'}".`,
        })
      }
    }
    // Build a row-level lookup of selections via group membership
    const rowSelections: Record<string, CoaSelection> = {}
    for (const g of classGroups) {
      const sel = classSelections[g.key]
      if (!sel) continue
      rowSelections[rowKey(g.main.tIdx, g.main.rIdx)] = sel
      for (const c of g.children) rowSelections[rowKey(c.tIdx, c.rIdx)] = sel
    }
    const updated = classifiedTables.map((t, tIdx) => ({
      ...t,
      rows: t.rows.map((r, rIdx) => {
        if (r.coa) return r
        const sel = rowSelections[rowKey(tIdx, rIdx)]
        if (!sel) return r
        return {
          ...r,
          category: sel.subcategory || r.category,
          general_category: sel.main_category || r.general_category,
          coa: {
            statement_type: sel.statement_type,
            main_category: sel.main_category,
            subcategory: sel.subcategory,
            account_code: sel.account_code,
          },
        }
      }),
    }))
    setClassifiedTables(updated)
    void submitClassifiedTables(updated)
  }

  async function handleCreateTaxonomy(draft: { statement_type: string; main_category: string; subcategory: string; account_code: string }) {
    await createSubcategory.mutateAsync({
      statement_type: draft.statement_type,
      main_category: draft.main_category,
      subcategory_name: draft.subcategory,
      account_code: draft.account_code,
    })
  }

  const submitClassifiedTables = useCallback(
    async (selected: ClassifiedTable[]) => {
      if (!s3FileId) return
      setStep('confirming')
      setStatusText('Saving to ledger…')
      const expected = selected.reduce((sum, t) => sum + t.rows.length, 0)
      try {
        await uploadApi.confirmUpload({
          s3_file_id: s3FileId,
          tables: selected,
          expected_transaction_count: expected,
        })
        // Poll for completion
        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (pollAbortRef.current.stop) throw new Error('Cancelled')
          await new Promise((r) => setTimeout(r, 3000))
          let s: StatusResponse
          try {
            s = await uploadApi.status(s3FileId)
          } catch {
            continue
          }
          const total = s.total_transactions ?? 0
          const processed = s.processed_transactions ?? 0
          if (total > 0) setStatusText(`Classifying - ${processed}/${total}…`)
          if (s.status === 'complete' || s.status === 'completed') break
          if (s.status === 'failed') throw new Error(s.error || 'Processing failed')
        }
        const final = await uploadApi.byFile(s3FileId)
        setResults(final)
        setStep('results')
      } catch (err) {
        toast({ variant: 'destructive', title: 'Processing failed', description: prettyErr(err) })
        setStep('review')
      }
    },
    [s3FileId, toast],
  )

  // Note: an earlier version aborted polling here on component unmount.
  // That triggered `throw new Error('Cancelled')` in the polling loops
  // whenever React 18 StrictMode (dev) ran its mount → cleanup → remount
  // double-invoke, causing legitimate uploads to display "Upload failed:
  // Cancelled" the moment the user clicked Upload. The polling loops are
  // owned by `pollAbortRef.current.stop` already, which is flipped only by
  // explicit user actions (resetWizard / Cancel buttons), so we don't need
  // an unmount-side abort. If a poll keeps running after navigation away,
  // its setState calls become no-ops in React 18 - harmless.

  const stats = useMemo(() => resultsStats(results), [results])

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  return (
    <Page
      title="Upload statement"
      description="Extract transactions from PDF, CSV, or Excel files and classify them."
      width="wide"
    >
      {limitReached ? (
        <Alert variant="destructive">
          <AlertTitle>Transaction limit reached</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>Upgrade your subscription to continue processing files.</span>
            <Button asChild variant="primary" size="sm">
              <Link to="/subscriptions">View plans</Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {step === 'form' ? (
        <Card className="p-4 sm:p-5">
          <form onSubmit={submitForm} className="flex flex-col gap-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormField
                id="stmt-type"
                label="Statement Type"
                required
                control={
                  <Select
                    value={statementType || undefined}
                    onValueChange={(v) => setStatementType(v as StatementTypeCode)}
                  >
                    <SelectTrigger id="stmt-type">
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BS">Bank Statement</SelectItem>
                      <SelectItem value="CCS">Credit Card Statement</SelectItem>
                    </SelectContent>
                  </Select>
                }
              />
              <FormField
                id="stmt-date"
                label="Statement Date"
                type="date"
                required
                value={period}
                onChange={(e) => setPeriod(e.currentTarget.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Bank account</label>
              <BankAccountPicker
                mode="single"
                value={bankAccountId}
                onChange={setBankAccountId}
                includeAddNew
                placeholder="Select a bank account or add new"
              />
              <p className="text-xs text-muted-foreground">
                First time? Click &quot;+ Add new account&quot; to record your bank + last-4. Next time it&apos;ll be in the dropdown.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Upload File</label>
              <div
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={cn(
                  'flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed p-8 text-center transition-colors',
                  dragOver ? 'border-primary bg-primary/5' : 'border-border',
                )}
              >
                <UploadCloud className="size-8 text-primary" />
                <h3 className="text-base font-semibold">Click to upload or drag &amp; drop</h3>
                <p className="text-xs text-muted-foreground">PDF, CSV, Excel · Max 10 MB</p>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.csv,.xlsx,.xls,.xlsb,.xlsm"
                  onChange={(e) => pickFile(e.currentTarget.files?.[0] ?? null)}
                  className="hidden"
                />
              </div>
              {file ? (
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/50 p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <FileUp className="size-4 text-primary" />
                    <span>{file.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove"
                    onClick={() => setFile(null)}
                  >
                    <X className="size-4" />
                  </Button>
                </div>
              ) : null}
            </div>

            <Button type="submit" variant="primary" className="w-full">
              <UploadCloud /> Upload and Extract Data
            </Button>
          </form>
        </Card>
      ) : null}

      {step === 'extracting' || step === 'confirming' ? (
        <Card className="p-8">
          <div className="flex flex-col items-center gap-4 text-center">
            <LoadingSpinner />
            <p className="text-base font-semibold text-primary">{statusText}</p>
            <Button type="button" variant="outline" size="sm" onClick={resetWizard}>
              Cancel
            </Button>
          </div>
        </Card>
      ) : null}

      {step === 'review' ? (
        <ReviewTablesCard
          tables={tables}
          excludedTables={excludedTables}
          onToggleExclude={toggleExcludeTable}
          onConfirm={confirmTables}
          onCancel={resetWizard}
        />
      ) : null}

      {step === 'classify' ? (
        <ClassifyStepper
          groups={classGroups}
          selections={classSelections}
          taxonomy={taxonomy}
          onSelect={(key, sel) => setClassSelections((prev) => ({ ...prev, [key]: sel }))}
          onClear={(key) => setClassSelections((prev) => ({ ...prev, [key]: null }))}
          onCreateRecord={handleCreateTaxonomy}
          onSave={saveClassifications}
          onCancel={resetWizard}
        />
      ) : null}

      {step === 'results' ? (
        <ResultsCard rows={results} stats={stats} onReset={resetWizard} />
      ) : null}

      {/* Consent modal */}
      <Modal
        open={consentOpen}
        onOpenChange={setConsentOpen}
        title="Upload Consent"
        description="Please review these terms before uploading."
        footer={
          <>
            <Button variant="secondary" onClick={() => setConsentOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                localStorage.setItem(TERMS_KEY, 'true')
                setConsentOpen(false)
                void executeUpload()
              }}
            >
              I Accept &amp; Proceed
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm">
          <p className="font-semibold">Data Processing Agreement &amp; Consent</p>
          <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
            <li>
              <strong>Authorization:</strong> You possess the necessary rights to upload and share
              the contents of the attached documents.
            </li>
            <li>
              <strong>AI Processing:</strong> You consent to OCR extraction, parsing, and
              categorization by our automated systems.
            </li>
            <li>
              <strong>Confidentiality:</strong> Data is processed via encrypted channels and visible
              only to you and authorized systems.
            </li>
            <li>
              <strong>Accuracy:</strong> You are responsible for verifying extracted data before
              using it for tax or legal purposes.
            </li>
            <li>
              <strong>Data Retention:</strong> Original files may be retained for processing
              verification and audit trails per our Privacy Policy.
            </li>
          </ul>
        </div>
      </Modal>

      {/* Duplicate resolution modal */}
      <DuplicateModal
        open={duplicateModalOpen}
        onClose={() => setDuplicateModalOpen(false)}
        duplicates={duplicates}
        newTxns={newTxns}
        onProceed={proceedWithFilter}
      />
    </Page>
  )
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────────

function ReviewTablesCard({
  tables,
  excludedTables,
  onToggleExclude,
  onConfirm,
  onCancel,
}: {
  tables: ExtractedTable[]
  excludedTables: Set<number>
  onToggleExclude: (idx: number, excluded: boolean) => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const [expandedTables, setExpandedTables] = useState<Set<number>>(new Set())
  const toggleExpanded = (idx: number) => {
    setExpandedTables((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }
  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-3">
        <h3 className="text-base font-semibold sm:text-lg">Review Detected Tables</h3>
        <p className="text-sm text-muted-foreground">
          We found the following activity tables. Exclude any you don&apos;t wish to process.
        </p>
      </div>
      <div className="flex flex-col gap-3">
        {tables.map((t, i) => {
          const excluded = excludedTables.has(i)
          const isExpanded = expandedTables.has(i)
          const preview = isExpanded ? t.rows : t.rows.slice(0, 5)
          return (
            <div
              key={i}
              className={cn(
                'rounded-md border p-3',
                excluded ? 'border-muted bg-muted/30 opacity-60' : 'border-border',
              )}
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={!excluded}
                    onCheckedChange={(v) => onToggleExclude(i, v === false)}
                    aria-label={`Include ${t.name}`}
                  />
                  <span className="font-semibold">{t.name}</span>
                  <Badge variant="secondary">{t.rows.length} rows</Badge>
                </div>
                {t.page ? (
                  <span className="text-xs text-muted-foreground">Page {t.page}</span>
                ) : null}
              </div>
              {preview.length > 0 ? (
                <div className="overflow-x-auto rounded border">
                  <Table className="w-full min-w-[480px] text-xs">
                    <thead className="bg-muted/60">
                      <tr>
                        <th className="px-2 py-1 text-left">Date</th>
                        <th className="px-2 py-1 text-left">Description</th>
                        <th className="px-2 py-1 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((r, j) => (
                        <tr key={j} className="border-t">
                          <td className="px-2 py-1">{r.date}</td>
                          <td className="max-w-[320px] truncate px-2 py-1">{r.description}</td>
                          <td className="px-2 py-1 text-right font-mono">{r.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : null}
              {t.rows.length > 5 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1 h-auto px-1 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => toggleExpanded(i)}
                >
                  {isExpanded ? 'Show less' : `… and ${t.rows.length - 5} more (show all)`}
                </Button>
              ) : null}
            </div>
          )
        })}
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" variant="primary" onClick={onConfirm}>
          Proceed with Selected Tables
        </Button>
      </div>
    </Card>
  )
}

function DuplicateModal({
  open,
  onClose,
  duplicates,
  newTxns,
  onProceed,
}: {
  open: boolean
  onClose: () => void
  duplicates: DuplicateRow[]
  newTxns: DuplicateRow[]
  onProceed: (filterFn: (tIdx: number, rIdx: number) => boolean) => Promise<void>
}) {
  // All new + optionally some duplicates are selected by default.
  const [checked, setChecked] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (open) {
      const s = new Set<string>()
      newTxns.forEach((r) => s.add(rowKey(r.originalTableIndex, r.originalRowIndex)))
      setChecked(s)
    }
  }, [open, newTxns])

  function toggle(row: DuplicateRow, value: boolean) {
    const k = rowKey(row.originalTableIndex, row.originalRowIndex)
    setChecked((prev) => {
      const next = new Set(prev)
      if (value) next.add(k)
      else next.delete(k)
      return next
    })
  }

  const renderRows = (list: DuplicateRow[]) => (
    <div className="overflow-x-auto rounded border">
      <Table className="w-full min-w-[520px] text-xs">
        <thead className="bg-muted/60">
          <tr>
            <th className="px-2 py-1" />
            <th className="px-2 py-1 text-left">Date</th>
            <th className="px-2 py-1 text-left">Description</th>
            <th className="px-2 py-1 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            const k = rowKey(r.originalTableIndex, r.originalRowIndex)
            return (
              <tr key={k} className="border-t">
                <td className="px-2 py-1">
                  <Checkbox
                    checked={checked.has(k)}
                    onCheckedChange={(v) => toggle(r, v === true)}
                  />
                </td>
                <td className="px-2 py-1">{r.date}</td>
                <td className="max-w-[320px] truncate px-2 py-1">{r.description}</td>
                <td className="px-2 py-1 text-right font-mono">{r.amount}</td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Review Duplicate Transactions"
      description="Some rows already exist. Review your selection before continuing."
      className="sm:max-w-3xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const newSet = new Set<string>(
                newTxns.map((r) => rowKey(r.originalTableIndex, r.originalRowIndex)),
              )
              void onProceed((tIdx, rIdx) => newSet.has(rowKey(tIdx, rIdx)))
            }}
          >
            Proceed with New Only
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              void onProceed((tIdx, rIdx) => checked.has(rowKey(tIdx, rIdx)))
            }
          >
            Proceed with Selected
          </Button>
        </>
      }
    >
      <div className="max-h-[60vh] space-y-4 overflow-auto">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-destructive">
            Duplicate transactions ({duplicates.length}) - unchecked by default
          </h4>
          {renderRows(duplicates)}
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-success">
            New transactions ({newTxns.length}) - checked by default
          </h4>
          {renderRows(newTxns)}
        </div>
      </div>
    </Modal>
  )
}

function ResultsCard({
  rows,
  stats,
  onReset,
}: {
  rows: StatementByFileRow[]
  stats: { income: number; expense: number }
  onReset: () => void
}) {
  const [showAll, setShowAll] = useState(false)
  if (rows.length === 0) {
    return (
      <Card className="p-6">
        <EmptyState title="No transactions saved" description="Please try again." />
      </Card>
    )
  }
  const visibleRows = showAll ? rows : rows.slice(0, 100)
  return (
    <Card className="p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2">
        <CheckCircle2 className="size-5 text-success" />
        <h3 className="text-base font-semibold sm:text-lg">
          Saved {rows.length} transaction{rows.length === 1 ? '' : 's'}
        </h3>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Total rows</div>
          <div className="mt-1 text-lg font-semibold">{rows.length}</div>
        </div>
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Income</div>
          <div className="mt-1 text-lg font-semibold text-success">
            {formatCurrencyPrecise(stats.income)}
          </div>
        </div>
        <div className="rounded-md border border-border bg-muted/40 p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">Expenses</div>
          <div className="mt-1 text-lg font-semibold text-destructive">
            {formatCurrencyPrecise(stats.expense)}
          </div>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <Table className="w-full min-w-[640px] text-xs">
          <thead className="bg-muted/60">
            <tr>
              <th className="px-2 py-1 text-left">Date</th>
              <th className="px-2 py-1 text-left">Description</th>
              <th className="px-2 py-1 text-right">Amount</th>
              <th className="px-2 py-1 text-left">Subcategory</th>
              <th className="px-2 py-1 text-left">Code</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r) => {
              const n = parseFloat(String(r.amount ?? 0)) || 0
              return (
                <tr key={r.id} className="border-t">
                  <td className="px-2 py-1">
                    {r.transaction_date ? formatDate(r.transaction_date) : '-'}
                  </td>
                  <td className="max-w-[280px] truncate px-2 py-1">{r.transaction_details}</td>
                  <td
                    className={cn(
                      'px-2 py-1 text-right font-mono',
                      n < 0 ? 'text-destructive' : 'text-success',
                    )}
                  >
                    {formatCurrencyPrecise(n)}
                  </td>
                  <td className="px-2 py-1">{r.classified_subcategory ?? '-'}</td>
                  <td className="px-2 py-1 font-mono">{r.classified_account_code ?? '-'}</td>
                </tr>
              )
            })}
          </tbody>
        </Table>
        {rows.length > 100 ? (
          <div className="flex items-center justify-center p-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-auto px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll
                ? `Show less (first 100 of ${rows.length})`
                : `Showing first 100 of ${rows.length} - show all`}
            </Button>
          </div>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <Button asChild variant="outline">
          <Link to="/view-statements">View all statements</Link>
        </Button>
        <Button type="button" variant="primary" onClick={onReset}>
          <RefreshCcw /> Upload another
        </Button>
      </div>
    </Card>
  )
}
